import React from 'react';

// Components
import AddressToInsure from './AddressToInsure';
import HomeAddressInputComponent from './HomeAddressInputComponent';
import HomeQuestions from './HomeQuestions';

// Types
import { Annex } from '../../../../../../../types/SMT';
import { Enterprise } from '../../../../../../../types/ProductFactory';
import ErrorPage from '../../../../../../common/src/components/ErrorPage';
import { Address } from '../../../../../../../types/Common';
import { request } from '../../../../../../common/src/legacy/services/httpSessionService';
import { isEmpty, omit } from 'lodash';
import { Coord } from '../../../../../../../types/MapBox';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useSubView from '../../../../hooks/useSubView';
import { prefillAnswer } from '../../../../store/redux/slices/flowsSlice';
import { useFlowsDispatch } from '../../../../../../common/src/utils/globalVariables';
import { useFlowsProgression } from '../../../../hooks/useFlowsProgression';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import { RoomEstimationProps } from '../../../../../../../types/ToolkitAddress';

interface Props {
  question: FlowsQuestion;
}

export interface HomeSearchAnswer {
  address: Address;
  cellar?: 'NONE' | 'INHABITABLE' | 'UNINHABITABLE';
  attic?: 'NONE' | 'INHABITABLE' | 'UNINHABITABLE';
  _local: {
    self: string | undefined;
    coordinates?: Coord[][];
    aerialImageLink?: string;
    isNotFoundInAPI?: boolean;
  };
}

export interface CompanySearchAnswer {
  'company.name': string;
  'company.address': Address;
  'company.cbe': string;
  'company.language': string;
  _local: Enterprise;
}

const HomeInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const {
    field: { value, onBlur, onChange },
    error,
  } = useFlowInputComponent({
    question,
  });
  const flowsDispatch = useFlowsDispatch();

  const { nextQuestion, previousQuestion } = useFlowsProgression();

  const defaultView = question.index ? 'pickHome' : 'addressToInsure';
  const {
    currentSubView,
    nextSubView,
    previousSubView,
    hasAnswer,
    changeSubView,
  } = useSubView({
    question,
    defaultView,
    defaultViewForDefinedAnswer: 'houseDetail',
  });

  const typedValue = value as HomeSearchAnswer | undefined;

  const updatePropertyInfo = async (propertyLink: string) => {
    const [resp, status] = await request({
      path: propertyLink,
      method: 'GET',
    });

    if (status === 200) {
      if (resp.has_solar_panels != null)
        flowsDispatch(
          prefillAnswer({
            id: 'home.has_solar_panels',
            answer: resp.has_solar_panels,
          }),
        );

      if (resp.has_swimming_pool != null)
        flowsDispatch(
          prefillAnswer({
            id: 'home.has_outside_swimming_pool',
            answer: resp.has_swimming_pool,
          }),
        );

      // If has porch is true, make sure the room value is at least 1
      if (resp.has_porch)
        flowsDispatch(
          prefillAnswer({
            id: 'home.rooms_veranda',
            answer: 1,
          }),
        );
    }
  };

  const handleOnFinishHomeInput = ({
    attic,
    cellar,
  }: {
    attic?: string;
    cellar?: string;
  }) => {
    onBlur({
      name: question.id,
      value: {
        address: omit(typedValue?.address, 'self'),
        cellar: cellar,
        attic: attic,
        _local: { ...typedValue?._local },
      },
    });

    nextQuestion();
  };

  const mapToCoordsObject = (coordinates: number[][]) => {
    if (coordinates?.length > 0)
      return [coordinates.map((array) => ({ lng: array[0], lat: array[1] }))];
    return [];
  };

  const updateRoomEstimation = async (url: string) => {
    const { data, isError } = await wgApi.get<RoomEstimationProps>(url);
    if (!isError) {
      Object.entries(data).forEach(([key, value]) => {
        if (key !== 'other_rooms')
          flowsDispatch(
            prefillAnswer({
              id: `home.rooms_${key}`,
              answer: value,
            }),
          );
      });
    }
  };

  const setHomeInformation = async (values: HomeSearchAnswer) => {
    let self;
    if (!values?._local.self && !isEmpty(values?.address)) {
      const {
        street,
        country_code,
        zipcode,
        housenr,
        city,
        boxnr,
        region,
        lat,
        lng,
      } = values!.address;
      self = `${
        process.env.REACT_APP_REST_HOST
      }v1/api/address/info/premium?street=${street || 'None'}&country_code=${
        country_code || 'None'
      }&zipcode=${zipcode || 'None'}&housenr=${housenr || 'None'}&boxnr=${
        boxnr || 'None'
      }&city=${city || 'None'}&lat=${lat || 'None'}&lng=${
        lng || 'None'
      }&region=${region || 'None'}`;
    } else {
      self = values?._local.self;
    }

    if (self) {
      const [resp, status] = await request({
        path: self?.replace('premium', 'freemium'),
        method: 'GET',
      });

      let aerialImageLink;

      if (status === 200) {
        const roomLink = resp?.data?.links.find(
          ({ rel }: { rel: string }) => rel === 'room_estimation',
        )?.href;
        const propertyLink = resp?.data?.links?.find(
          ({ rel }: { rel: string }) => rel === 'properties',
        )?.href;

        aerialImageLink = resp?.data?.links?.find(
          ({ rel }: { rel: string }) =>
            rel === 'aerial_images_marked_satellite',
        )?.href;

        if (propertyLink) updatePropertyInfo(propertyLink);
        if (roomLink) updateRoomEstimation(roomLink);

        if (resp?.data?.parcel?.annexes?.length > 0) {
          flowsDispatch(
            prefillAnswer({
              id: 'home.annexes',
              answer: resp?.data?.parcel?.annexes?.map((annex: Annex) => ({
                ...annex,
                type: annex.type?.toUpperCase(),
              })),
            }),
          );
        }

        if (resp?.data?.parcel?.main_building?.house_type) {
          flowsDispatch(
            prefillAnswer({
              id: 'home.attachment',
              answer:
                resp?.data?.parcel?.main_building?.house_type?.toUpperCase(),
            }),
          );
        }

        if (resp?.data?.parcel?.main_building?.construction_year_category) {
          flowsDispatch(
            prefillAnswer({
              id: 'home.age',
              answer:
                resp?.data?.parcel?.main_building?.construction_year_category?.toUpperCase(),
            }),
          );
        }
      }
      onBlur({
        name: question.id,
        value: {
          ...values,
          _local: {
            ...values?._local,
            ...(resp?.data?.parcel?.main_building?.coordinates && {
              coordinates: mapToCoordsObject(
                resp?.data?.parcel?.main_building?.coordinates,
              ),
            }),
            ...(aerialImageLink && {
              aerialImageLink,
            }),
          },
          ...(resp?.data?.address && { address: resp?.data?.address }),
        },
      });
    } else {
      onBlur({
        name: question.id,
        value: {
          ...values,
          _local: {
            ...values?._local,
            isNotFoundInAPI: true,
          },
        },
      });
    }
  };

  const handleNewSearch = () => {
    changeSubView(defaultView);
    onChange({ name: question.id, value: {} });
  };

  return (
    <>
      {{
        addressToInsure: (
          <AddressToInsure
            handleBlur={onBlur}
            question={question}
            nextSubView={nextSubView}
            setHomeInformation={setHomeInformation}
            previousQuestionHandler={previousQuestion}
          />
        ),
        pickHome: (
          <HomeAddressInputComponent
            question={question}
            value={typedValue}
            error={error?.message}
            onChange={onChange}
            nextSubView={nextSubView}
            setHomeInformation={setHomeInformation}
            previousSubView={
              question.index ? previousQuestion : previousSubView
            }
          />
        ),
        houseDetail: (
          <HomeQuestions
            previousSubView={hasAnswer ? previousQuestion : previousSubView}
            nextQuestionHandler={handleOnFinishHomeInput}
            address={typedValue?.address}
            atticVal={typedValue?.attic}
            cellarVal={typedValue?.cellar}
            handleNewSearch={handleNewSearch}
          />
        ),
      }[currentSubView] || <ErrorPage />}
    </>
  );
};

export default HomeInputComponent;
